import React, { useEffect, useState, useRef } from 'react';
import { Container, Button } from '../Layout';
import { Divider } from './styles';
import i18n from '../../helpers/i18n';
import { getLocationsAndCnpj, createMultipleDevices } from '../CreateDeviceContent/CreateDeviceContent.queries';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { validationMultipleSchema } from './validationMultipleSchema';
import MultipleDevicesCreatedAlert from '../MultipleDevicesCreatedAlert/';
import LoadingOverlay from '../LoadingOverlay';
import { useNavigate } from 'react-router-dom';
import { useError } from '../SnackbarError/ErrorContext';
import { usePermissions } from '../../hooks/usePermissions';
import { ControllerSection, RestrictionSection, MultipleDeviceSection } from '../CreateEditSections';
import { Grid } from '@mui/material';

const MultipleDevicesContent = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [cnpjs, setCnpjs] = useState([]);
    const [isOpenConfirmAlert, setIsOpenConfirmAlert] = useState(false);
    const [devicesLenght, setDevicesLenght] = useState(0);
    const [showLoading, setShowLoading] = useState(false);
    const [company, setCompany] = useState('');

    const { permissions } = usePermissions();
    const { showError } = useError();

    const { data: dataCnpjs } = useQuery({
        queryKey: ['locations'],
        queryFn: getLocationsAndCnpj,
        refetchOnWindowFocus: false,
        retry: false
    });

    const mutationCreateMultipleDevice = useMutation({
        mutationFn: createMultipleDevices,
        onSuccess: (response) => {
            setShowLoading(false);
            if (response.result) {
                setDevicesLenght(response.employees.length);
                setIsOpenConfirmAlert(true);
            } else {
                let errorMsg = `${i18n.t('pages.errors.device.multipleDevicesDefault')}, erro: ${response.errorType}`
                if (response.errorType === 'all_codes_have_been_created_ahgora_online_device') {
                    errorMsg = i18n.t('pages.errors.device.all_codes_have_been_created_ahgora_online_device')
                } if (response.errorType === 'all_codes_have_been_created_ahgora_multi_device') {
                    errorMsg = i18n.t('pages.errors.device.all_codes_have_been_created_ahgora_multi_device')
                }
                showError(errorMsg);
            }
        },
        onError: () => {
            showError(i18n.t('pages.errors.device.multipleDevicesDefault'));
        },
    });

    const formik = useFormik({
        initialValues: {
            deviceType: '',
            cnpj: '',
            timezone: 'America/Sao_Paulo',
            activationKey: '',
            expirationDate: false,
            startPeriod: null,
            endPeriod: null,

            //Controller
            enable_password_punch: false,
            timezone_filter: false,
            save_photo_on_punch: false,

            //Restriction
            online_only: false,
            auto_timezone: false,
            only_with_location: false,
            only_inside_geofence: false,
            range_ip: false,
            ips: [],
        },
        validationSchema: validationMultipleSchema,
        onSubmit: values => {
            setShowLoading(true);
            const updatedValues = {
                ...values,
                range_ip: values.ips.some(ip => ip.trim().length > 0)
            };
            // Ajusta a flag antes do envio conforme o tipo do dispositivo
            const adjustedValues = values.deviceType === 'ahgora_online_device'
                ? { ...updatedValues, only_with_location: false }
                : updatedValues;
            mutationCreateMultipleDevice.mutate({ deviceData: { ...adjustedValues, company }, queryClient });
        }
    });

    const initializedLocationFlagRef = useRef(false);

    useEffect(() => {
        if (initializedLocationFlagRef.current) return;
        if (permissions?.batida_por_geolocalizacao_obrigatoria) {
            formik.setFieldValue('only_with_location', true);
            initializedLocationFlagRef.current = true;
        }
    }, [permissions, formik]);

    useEffect(() => {
        if (dataCnpjs && dataCnpjs.cnpjs.length > 0) {
            const cnpjArray = dataCnpjs.cnpjs.map(cnpjItem => ({ 
                value: cnpjItem.cnpj, 
                title: `${cnpjItem.cnpj} - ${cnpjItem.razao_social}` 
            }));
            setCnpjs(cnpjArray);
            setCompany(dataCnpjs.company);

            if (!formik.values.cnpj) {
                formik.setFieldValue('cnpj', cnpjArray[0].value); 
            }
        }
    }, [dataCnpjs, formik]);

    function handleCreatedMultipleDevice() {
        setIsOpenConfirmAlert(false);
        navigate('/');
    }

    return (
        <Container>
            <LoadingOverlay isLoading={showLoading} />
            <MultipleDevicesCreatedAlert isOpen={isOpenConfirmAlert} onCreated={handleCreatedMultipleDevice} devicesLenght={devicesLenght} />

            <form style={{ width: '100%' }} onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <MultipleDeviceSection 
                            formik={formik}
                            cnpjs={cnpjs}
                            isLoadingLocationsAndCnpjs={false}
                        />
                    </Grid>

                    {formik.values.deviceType && (
                        <Grid item xs={12} md={6}>
                            <ControllerSection formik={formik} permissions={permissions} isMultipleCreation={true} />
                            {permissions?.permite_restricao_batida && (
                                <RestrictionSection formik={formik} permissions={permissions}/>
                            )}
                        </Grid>
                    )}
                </Grid>
                <Divider></Divider>
                <Divider style={{ borderTop: 'solid 1px var(--ahg-gray-lighten4)', paddingTop: 16, marginTop: 16 }}>
                    <Button
                        type="outlined"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/');
                        }}
                    >{i18n.t('pages.multipleDevice.cancel')}</Button>
                    <Button
                        type="submit"
                    >{i18n.t('pages.multipleDevice.createDevice')}
                    </Button>
                </Divider>
            </form>
        </Container>
    );
};

export default MultipleDevicesContent;
